import React, { useEffect, useState } from "react";
import {
  Box, Grid, TextField, Typography, CircularProgress, Switch, InputAdornment, Paper,
} from "@mui/material";
import { useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import DescriptionIcon from "@mui/icons-material/Description";
import StarIcon from "@mui/icons-material/Star";
import ImageIcon from "@mui/icons-material/Image";
import { useCreateCompanyMutation, useGetCompanyQuery } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { ICompanyDetails } from "../../../Api/Interface/api.interface";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { useNavigate, useParams } from "react-router-dom";
import {
  AppPage, PageHeader, FormCard, UploadBox, ActionButton,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const LARAVEL_API_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const CompanyEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [updateCompany, { isLoading }] = useCreateCompanyMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedLogo, setSelectedLogo] = useState<File | undefined>(undefined);
  const [selectedWatermark, setSelectedWatermark] = useState<File | undefined>(undefined);
  const [existingLogo, setExistingLogo] = useState("");
  const [existingWatermark, setExistingWatermark] = useState("");
  const [addressSearch, setAddressSearch] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressRef = React.useRef<HTMLDivElement>(null);

  const { data: companyData, isLoading: dataLoading } = useGetCompanyQuery({ searchText: "", id: id || "" });

  // Load existing data
  useEffect(() => {
    if (!companyData?.data) return;
    const list = Array.isArray(companyData.data) ? companyData.data : [companyData.data];
    const company = list.find((c: any) => String(c.id) === id) as ICompanyDetails | undefined;
    if (!company) return;

    formik.setValues({
      companyName: company.companyName || "",
      companySlog: company.companySlog || "",
      companyProvince: company.companyProvince || "",
      companyCity: company.companyCity || "",
      companyPostalCode: company.companyPostalCode || "",
      companyStreetNumber: company.companyStreetNumber || "",
      companyStreetName: company.companyStreetName || "",
      companyEmail: company.companyEmail || "",
      companyMobileNumber: company.companyMobileNumber || "",
      companyAltMobileNumber: company.companyAltMobileNumber || "",
      companyWebSiteLink: company.companyWebSiteLink || "",
      companyHstNo: company.companyHstNo || "",
      companyDescription: company.companyDescription || "",
      companyFooterDetails: company.companyFooterDetails || "",
      isDefault: company.isDefault === 1,
    });
    if (company.CompanyLogo) setExistingLogo(company.CompanyLogo);
    if (company.CompanyWatermark) setExistingWatermark(company.CompanyWatermark);
  }, [companyData, id]);

  // Click outside to close suggestions
  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (addressRef.current && !addressRef.current.contains(e.target as Node)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Address autocomplete
  React.useEffect(() => {
    if (addressSearch.length < 3) { setAddressSuggestions([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=ca,us&limit=5&q=${encodeURIComponent(addressSearch)}`);
        setAddressSuggestions(await res.json());
        setShowSuggestions(true);
      } catch { setAddressSuggestions([]); }
    }, 400);
    return () => clearTimeout(timer);
  }, [addressSearch]);

  const handleAddressSelect = (place: any) => {
    const addr = place.address || {};
    formik.setFieldValue("companyStreetNumber", addr.house_number || "");
    formik.setFieldValue("companyStreetName", addr.road || "");
    formik.setFieldValue("companyCity", addr.city || addr.town || addr.village || addr.municipality || "");
    formik.setFieldValue("companyProvince", addr.state || addr.province || "");
    formik.setFieldValue("companyPostalCode", addr.postcode || "");
    setAddressSearch(place.display_name || "");
    setShowSuggestions(false);
  };

  const handlePhoneChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(field, formatPhone(e.target.value));
  };

  const formik = useFormik({
    initialValues: {
      companyName: "", companySlog: "", companyProvince: "", companyCity: "",
      companyPostalCode: "", companyStreetNumber: "", companyStreetName: "",
      companyEmail: "", companyMobileNumber: "", companyAltMobileNumber: "",
      companyWebSiteLink: "", companyHstNo: "", companyDescription: "",
      companyFooterDetails: "", isDefault: false,
    },
    enableReinitialize: false,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.companyName.trim()) errors.companyName = "Company name is required";
      if (values.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.companyEmail)) errors.companyEmail = "Invalid email";
      return errors;
    },
    onSubmit: async (values) => {
      try {
        let CompanyLogo: string | undefined = existingLogo || undefined;
        let CompanyWatermark: string | undefined = existingWatermark || undefined;
        if (selectedLogo) CompanyLogo = (await uploadImage(selectedLogo)) || "";
        if (selectedWatermark) CompanyWatermark = (await uploadImage(selectedWatermark)) || "";

        const res = await updateCompany({ id, ...values, CompanyLogo, CompanyWatermark }).unwrap();
        if (!res.status) showErrorMessage(res.message);
        else { showMessage("Company updated successfully"); navigate("/company/list"); }
      } catch { showErrorMessage("Something went wrong"); }
    },
  });

  const formValid = formik.values.companyName.trim() !== "";

  const handleImageChange = (setter: (f: File | undefined) => void) => (file: File) => {
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"].includes(file.type)) { showErrorMessage("Must be an image"); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("Must be under 2 MB"); return; }
    setter(file);
  };

  if (dataLoading) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <CircularProgress size={28} sx={{ color: NAVY }} />
    </Box>
  );

  // Resolve preview URLs for UploadBox
  const logoPreview = selectedLogo
    ? URL.createObjectURL(selectedLogo)
    : existingLogo ? `${LARAVEL_API_URL}/${existingLogo}` : undefined;
  const watermarkPreview = selectedWatermark
    ? URL.createObjectURL(selectedWatermark)
    : existingWatermark ? `${LARAVEL_API_URL}/${existingWatermark}` : undefined;

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<BusinessIcon />}
          title="Edit Company"
          subtitle="Update company profile details"
          actions={
            <ActionButton
              tone="navy"
              startIcon={<SaveIcon />}
              disabled={!formValid || isLoading}
              onClick={() => formik.handleSubmit()}
            >
              {isLoading ? "Saving..." : "Update Company"}
            </ActionButton>
          }
        />
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* LEFT — main form */}
          <Grid item xs={12} lg={8}>
            {/* Company Details */}
            <Box mb={3}>
              <FormCard
                title="Company Details"
                subtitle="Name, slogan, website and tax number"
                icon={<BusinessIcon />}
                required
              >
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Company Name" required
                      placeholder="Enter company name"
                      {...formik.getFieldProps("companyName")}
                      error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                      helperText={formik.touched.companyName && formik.errors.companyName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Slogan / Tagline" placeholder="Your company slogan" {...formik.getFieldProps("companySlog")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Website" placeholder="https://example.com" {...formik.getFieldProps("companyWebSiteLink")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="HST / Tax Number" placeholder="HST number" {...formik.getFieldProps("companyHstNo")} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Switch size="small" checked={formik.values.isDefault as boolean} onChange={(_, v) => formik.setFieldValue("isDefault", v)} />
                      <StarIcon sx={{ fontSize: 16, color: formik.values.isDefault ? "#f59e0b" : "text.disabled" }} />
                      <Typography fontSize={13} fontWeight={600} color={formik.values.isDefault ? "#f59e0b" : "text.secondary"}>
                        Set as default company
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </FormCard>
            </Box>

            {/* Contact Information */}
            <Box mb={3}>
              <FormCard
                title="Contact Information"
                subtitle="Email address and phone numbers"
                icon={<PhoneIcon />}
              >
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth size="small" label="Email" type="email" placeholder="company@example.com"
                      {...formik.getFieldProps("companyEmail")}
                      error={formik.touched.companyEmail && Boolean(formik.errors.companyEmail)}
                      helperText={formik.touched.companyEmail && formik.errors.companyEmail}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth size="small" label="Phone" placeholder="(555) 000-0000"
                      value={formik.values.companyMobileNumber}
                      onChange={handlePhoneChange("companyMobileNumber")}
                      onBlur={formik.handleBlur} name="companyMobileNumber"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth size="small" label="Alt Phone" placeholder="(555) 000-0000"
                      value={formik.values.companyAltMobileNumber}
                      onChange={handlePhoneChange("companyAltMobileNumber")}
                      onBlur={formik.handleBlur} name="companyAltMobileNumber"
                    />
                  </Grid>
                </Grid>
              </FormCard>
            </Box>

            {/* Address */}
            <Box mb={3}>
              <FormCard
                title="Address"
                subtitle="Physical location of the company"
                icon={<LocationOnIcon />}
              >
                {/* Address Search */}
                <Box ref={addressRef} sx={{ position: "relative", mb: 2.5 }}>
                  <TextField
                    fullWidth size="small" label="Search Address" placeholder="Start typing an address..."
                    value={addressSearch} onChange={e => setAddressSearch(e.target.value)}
                    onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {showSuggestions && addressSuggestions.length > 0 && (
                    <Paper elevation={3} sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, mt: 0.5, borderRadius: 2.5, border: "1px solid", borderColor: "divider", maxHeight: 220, overflowY: "auto" }}>
                      {addressSuggestions.map((place, i) => (
                        <Box key={i} onClick={() => handleAddressSelect(place)}
                          sx={{ px: 2, py: 1.25, cursor: "pointer", borderBottom: "1px solid", borderColor: "divider", "&:hover": { bgcolor: "action.hover" }, display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <LocationOnIcon sx={{ fontSize: 14, color: NAVY, mt: 0.25, flexShrink: 0 }} />
                          <Typography fontSize={12} color="text.secondary" lineHeight={1.4}>{place.display_name}</Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}
                </Box>

                <Grid container spacing={2.5}>
                  <Grid item xs={4} sm={2}>
                    <TextField fullWidth size="small" label="Street #" placeholder="#" {...formik.getFieldProps("companyStreetNumber")} />
                  </Grid>
                  <Grid item xs={8} sm={4}>
                    <TextField fullWidth size="small" label="Street Name" placeholder="Main St" {...formik.getFieldProps("companyStreetName")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="City" placeholder="City" {...formik.getFieldProps("companyCity")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Province / State" placeholder="Province" {...formik.getFieldProps("companyProvince")} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Postal Code" placeholder="A1B 2C3" {...formik.getFieldProps("companyPostalCode")} />
                  </Grid>
                </Grid>
              </FormCard>
            </Box>

            {/* Additional Details */}
            <Box mb={3}>
              <FormCard
                title="Additional Details"
                subtitle="Description and invoice footer text"
                icon={<DescriptionIcon />}
              >
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Description" placeholder="About this company..." multiline rows={3} {...formik.getFieldProps("companyDescription")} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Invoice Footer Text" placeholder="Payment terms, thank you notes..." multiline rows={2} {...formik.getFieldProps("companyFooterDetails")} />
                  </Grid>
                </Grid>
              </FormCard>
            </Box>
          </Grid>

          {/* RIGHT — branding & save */}
          <Grid item xs={12} lg={4}>
            {/* Company Logo */}
            <Box mb={3}>
              <FormCard title="Company Logo" subtitle="Displayed on invoices and reports" icon={<ImageIcon />}>
                <UploadBox
                  previewUrl={logoPreview}
                  onFile={handleImageChange(setSelectedLogo)}
                  hint="JPG, PNG, WEBP up to 2 MB"
                  title="Upload Logo"
                />
              </FormCard>
            </Box>

            {/* Invoice Watermark */}
            <Box mb={3}>
              <FormCard title="Invoice Watermark" subtitle="Faint background image on PDF invoices" icon={<ImageIcon />}>
                <UploadBox
                  previewUrl={watermarkPreview}
                  onFile={handleImageChange(setSelectedWatermark)}
                  hint="JPG, PNG, WEBP up to 2 MB"
                  title="Upload Watermark"
                />
              </FormCard>
            </Box>

            {/* Save action */}
            <ActionButton
              tone="navy"
              fullWidth
              startIcon={<SaveIcon />}
              disabled={!formValid || isLoading}
              onClick={() => formik.handleSubmit()}
              sx={{ py: 1.5, fontSize: 14 }}
            >
              {isLoading ? "Saving..." : "Update Company"}
            </ActionButton>
          </Grid>
        </Grid>
      </form>
    </AppPage>
  );
};

export default CompanyEdit;
